import React, { useState, useEffect, useCallback } from 'react';
import { getMyDonationHistory } from '../../services/donationService';
import { Donation } from '../../types/interfaces';
import { Link, useNavigate } from 'react-router-dom';
import BackButton from '../../components/common/BackButton';

interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/**
 * Simple Pagination component to replace React Bootstrap's Pagination
 * to avoid TypeScript errors
 */
const SimplePagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}> = ({ currentPage, totalPages, onPageChange, disabled = false }) => {
  // Generate an array of page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    
    // Always show first page
    if (currentPage > 2) {
      pages.push(1);
    }
    
    // Add ellipsis if needed
    if (currentPage > 3) {
      pages.push('ellipsis1');
    }
    
    // Show previous page if not on first page
    if (currentPage > 1) {
      pages.push(currentPage - 1);
    }
    
    // Current page
    pages.push(currentPage);
    
    // Show next page if not on last page
    if (currentPage < totalPages) {
      pages.push(currentPage + 1);
    }
    
    // Add ellipsis if needed
    if (currentPage < totalPages - 2) {
      pages.push('ellipsis2');
    }
    
    // Always show last page if more than one page
    if (currentPage < totalPages - 1 && totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  };
  
  const pageNumbers = getPageNumbers();
  
  return (
    <nav aria-label="Donation history pagination">
      <ul className="pagination justify-content-center">
        {/* Previous button */}
        <li className={`page-item ${currentPage === 1 || disabled ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || disabled}
            aria-label="Previous page"
          >
            &laquo; Previous
          </button>
        </li>
        
        {/* Page numbers */}
        {pageNumbers.map((page, index) => {
          if (page === 'ellipsis1' || page === 'ellipsis2') {
            return (
              <li key={`ellipsis-${index}`} className="page-item disabled">
                <span className="page-link">...</span>
              </li>
            );
          }
          
          return (
            <li 
              key={`page-${page}`} 
              className={`page-item ${currentPage === page ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
            >
              <button
                className="page-link"
                onClick={() => onPageChange(page as number)}
                disabled={disabled || currentPage === page}
              >
                {page}
              </button>
            </li>
          );
        })}
        
        {/* Next button */}
        <li className={`page-item ${currentPage === totalPages || disabled ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages || disabled}
            aria-label="Next page"
          >
            Next &raquo;
          </button>
        </li>
      </ul>
    </nav>
  );
};

/**
 * Component for displaying user's donation history
 */
const DonationsPage: React.FC = () => {
  // State
  const [donations, setDonations] = useState<Donation[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Function to fetch donations using useCallback to prevent infinite loops
  const fetchDonations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getMyDonationHistory(pagination.page, pagination.limit);
      
      // Type assertion for the API response
      setDonations(result.donations);
      setPagination({
        total: result.total || 0,
        page: result.page || 1,
        limit: result.limit || 10,
        pages: result.pages || 0
      });
    } catch (err) {
      console.error('Error fetching donations:', err);
      setError('Failed to load donation history. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  // Effect to load donations on mount and when page/filters change
  useEffect(() => {
    fetchDonations();
  }, [fetchDonations]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= pagination.pages) {
      setPagination(prev => ({
        ...prev,
        page: newPage
      }));
    }
  };

  // Format date to readable string
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format currency amount
  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  return (
    <div className="container py-5">
      <BackButton 
        label="Back to Profile" 
        onClick={() => navigate('/contributor/profile')}
      />
      
      <div className="row mb-4">
        <div className="col-md-8">
          <h1 className="mb-0">Donation History</h1>
          <p className="text-muted">View your contribution history</p>
        </div>
        <div className="col-md-4 text-md-end">
          <Link to="/contributor/profile" className="btn btn-outline-primary">
            <i className="fas fa-user me-2"></i>Back to Profile
          </Link>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setError(null)} 
            aria-label="Close"
          ></button>
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0">Your Donations</h5>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading donation history...</p>
            </div>
          ) : donations.length === 0 ? (
            <div className="text-center py-4">
              <p className="mb-0">You haven't made any donations yet.</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Campaign</th>
                      <th>Amount</th>
                      <th>Payment Method</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {donations.map(donation => (
                      <tr key={donation._id}>
                        <td>{formatDate(donation.donationDate)}</td>
                        <td>{donation.campaignName || 'General Donation'}</td>
                        <td>{formatAmount(donation.amount, donation.currency)}</td>
                        <td>
                          {donation.paymentMethod}
                          {donation.isRecurring && (
                            <span className="badge bg-info ms-2">Recurring</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${donation.status === 'processed' ? 'bg-success' : 'bg-secondary'}`}>
                            {donation.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {pagination.pages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <SimplePagination
                    currentPage={pagination.page}
                    totalPages={pagination.pages}
                    onPageChange={handlePageChange}
                    disabled={isLoading}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonationsPage; 